<template>
    <div class="container">
        <h1 class="mt-3">{{ title }}</h1>

        <div class="input-group mb-3 rounded-pill">
            <input type="text" class="form-control" :placeholder="$t('task.searchPlaceholder')" v-model="searchTerm"
                @keyup.enter.prevent="onSearchSubmit" />
            <button class="input-group-text bg-white" type="button" @click="onSearchSubmit">
                <i class="fa-solid fa-magnifying-glass"></i>
            </button>
        </div>

        <!-- Filter Groups -->
        <FilterGroups v-if="showFilters" :filterGroups="filterGroups" @filters-changed="onFiltersChanged" />

        <TaskList :tasks="tasks" />

        <ListPagination :currentPage="currentPage" :totalTasks="totalTasks" :limit="limit"
            @page-changed="onPageChanged" />
    </div>
</template>

<script>
import axios from '@/services/axios';
import TaskList from '@/components/TaskList.vue';
import ListPagination from '@/components/ListPagination.vue';
import FilterGroups from '@/components/FilterGroups.vue';

export default {
    name: 'TasksListComponent',
    components: { TaskList, ListPagination, FilterGroups },
    props: {
        title: String,
        apiUrl: String,
        showFilters: { type: Boolean, default: false }
    },
    data() {
        return {
            tasks: [],
            currentPage: 1,        // mirror of ?page=
            limit: 10,
            totalTasks: 0,
            searchTerm: '',        // bound to input; mirrors ?search=
            activeFilters: [],
            lastRouteKey: '',     // prevents duplicate fetches when multiple query keys change together

            /* your filterGroups unchanged */
            filterGroups: [
                {
                    name: '',
                    multi: false,
                    filters: [
                        { key: 'stage0', labelKey: 'enums.stage.0', active: false, activeClass: 'bg-warning-subtle text-warning', inactiveClass: 'text-bg-light', params: { stage_id: '0', finished: '0' } },
                        { key: 'stage1', labelKey: 'enums.stage.1', active: false, activeClass: 'bg-info-subtle text-info', inactiveClass: 'text-bg-light', params: { stage_id: '1', finished: '0' } },
                        { key: 'stage2', labelKey: 'enums.stage.2', active: false, activeClass: 'bg-success-subtle text-success', inactiveClass: 'text-bg-light', params: { stage_id: '2', finished: '0' } },
                        { key: 'finished', labelKey: 'enums.stage.finished', active: false, activeClass: 'bg-success-subtle text-success', inactiveClass: 'text-bg-light', params: { finished: '1' } },
                    ]
                },
                {
                    name: '',
                    multi: true,
                    filters: [
                        { key: 'priority1', labelKey: 'enums.priority.1', active: false, activeClass: 'text-bg-dark', inactiveClass: 'text-bg-light', params: { priority_id: '1' } },
                        { key: 'priority2', labelKey: 'enums.priority.2', active: false, activeClass: 'text-bg-dark', inactiveClass: 'text-bg-light', params: { priority_id: '2' } },
                        { key: 'priority3', labelKey: 'enums.priority.3', active: false, activeClass: 'text-bg-dark', inactiveClass: 'text-bg-light', params: { priority_id: '3' } }
                    ]
                }
            ]
        };
    },

    mounted() {
        // Load saved filters on first render (does NOT fetch)
        if (this.showFilters) this.loadSelectedFilters();
    },

    watch: {
        // Single place that reacts to URL changes (both page + search together).
        '$route.query': {
            immediate: true,
            deep: true,
            handler(newQ) {
                // normalize page
                const pRaw = newQ.page ?? '1';
                const p = parseInt(pRaw, 10);
                if (!Number.isFinite(p) || p < 1) {
                    this.$router.replace({ query: { ...newQ, page: '1' } });
                    return; // wait for the normalized route to re-trigger
                }

                // normalize search
                const s = (newQ.search ?? '').toString();

                // keep input in sync with URL
                if (this.searchTerm !== s) this.searchTerm = s;

                const key = `${p}|${s}`;
                if (key === this.lastRouteKey) return; // prevent duplicate fetches
                this.lastRouteKey = key;

                this.currentPage = p;
                this.fetchTasks(p, s);
            }
        }
    },

    methods: {
        // User pressed enter or clicked the search button
        onSearchSubmit() {
            const term = (this.searchTerm || '').trim();
            const q = { ...this.$route.query, page: '1' };
            if (term) q.search = term; else delete q.search;
            this.$router.push({ query: q });
            // Do NOT call fetch here; watcher will fetch once.
        },

        onFiltersChanged(activeFilters) {
            this.activeFilters = activeFilters;
            // Reset to first page when filters change, keep current search
            const q = { ...this.$route.query, page: '1' };
            this.$router.push({ query: q });
            this.saveSelectedFilters();
            this.fetchTasks(1, this.searchTerm);
            // Do NOT call fetch here; watcher will fetch once.
        },

        // Pagination click from ListPagination
        onPageChanged(newPage) {
            const q = { ...this.$route.query, page: String(newPage) };
            if (q.page !== this.$route.query.page) {
                this.$router.push({ query: q });
            }
        },

        async fetchTasks(page = 1, searchTerm = '') {
            try {
                let url = `${this.apiUrl}&page=${page}&limit=${this.limit}&search=${encodeURIComponent(searchTerm)}`;
                
                if (this.showFilters) this.loadSelectedFilters();
                if (this.showFilters) {
                    const mergedParams = this.mergeFilterParams(this.activeFilters);
                    Object.keys(mergedParams).forEach(key => {
                        url += `&${key}=${encodeURIComponent(mergedParams[key])}`;
                    });
                }

                const response = await axios.get(url);
                this.tasks = response.data.data;
                this.totalTasks = response.data.total;
                this.currentPage = page;
            } catch (error) {
                console.error(`Error fetching tasks for ${this.title}:`, error);
            }
        },

        mergeFilterParams(filters) {
            const paramMap = {};
            filters.forEach(f => {
                Object.entries(f.params).forEach(([key, rawValue]) => {
                    const values = String(rawValue).split(',');
                    (paramMap[key] ||= []).push(...values);
                });
            });

            const merged = {};
            Object.keys(paramMap).forEach(key => {
                const uniqueValues = [...new Set(paramMap[key])];
                merged[key] = uniqueValues.join(',');
            });
            return merged;
        },

        saveSelectedFilters() {
            const activeKeys = this.activeFilters.map(f => f.key);
            localStorage.setItem('taskListFilterKeys', JSON.stringify(activeKeys));
        },

        loadSelectedFilters() {
            const stored = localStorage.getItem('taskListFilterKeys');
            if (!stored) return;
            try {
                const keysArray = JSON.parse(stored);
                this.filterGroups.forEach(group => {
                    group.filters.forEach(filter => {
                        filter.active = keysArray.includes(filter.key);
                    });
                });
            } catch (err) {
                console.warn('Could not parse saved filter keys:', err);
            }
            this.collectActiveFilters();
        },

        collectActiveFilters() {
            const active = [];
            this.filterGroups.forEach(group => {
                group.filters.forEach(filter => {
                    if (filter.active) active.push(filter);
                });
            });
            this.activeFilters = active;
        }
    }
};
</script>
